import {
  ProjectTaskNode, ProjectTaskRow, ProjectTasksResponse,
} from '../../interfaces/ProjectTasksInterface';

/*
 * Task-claim picker — pure filtering + roll-up arithmetic. No React, no DOM.
 *
 * Semantics are `src/util/projectsFilter.ts`'s `filterTasksRegion`, ported to
 * the `/tasks` payload so tree mode behaves exactly like the Projects page
 * payroll already knows (design Q5, "reveal in place"):
 *
 *   - lowercase, trimmed, plain substring. No fuzzy matching, no word
 *     boundaries, and never a RegExp built from user input.
 *   - each row is reduced to ONE flat string whose fields are joined by a
 *     separator a term cannot type across, so a term can never bridge fields.
 *   - a node that matches on its own fields reveals its WHOLE subtree;
 *   - a node that doesn't survives only as `context` if a descendant node or
 *     any task beneath it matched (dimmed in the UI);
 *   - a node with no matching descendants is dropped entirely.
 *
 * The design brief called out two gaps in the original blob, closed here:
 * `altTaskID` (the piece code — the single most likely thing to type) and
 * `intermediatePath` (the breadcrumb). The account code is in too, because the
 * zero-result copy promises "piece code, description, account code and the
 * intermediate path".
 */

/** Field separator: characters a term can't produce by typing across two values. */
const SEP = ' · ';

const searchable = (...parts: (string | null | undefined)[]): string =>
  parts.filter((p) => !!p).join(SEP).toLowerCase();

export const normalizeTerm = (term: string): string => term.trim().toLowerCase();

/**
 * Row identity. `taskID` alone is NOT unique: the API returns one row per
 * (task, intermediate) link, so a task budgeted once but linked to two
 * intermediates comes back twice under two breadcrumbs. Everything that
 * addresses a row — selection, claimant expansion, keyboard cursor — keys on
 * this, never on `taskID`.
 */
export const taskKey = (row: ProjectTaskRow): string =>
  `${row.taskID}:${row.intermediateID ?? 'direct'}`;

/** What a term is matched against on a task row. */
export const taskBlob = (t: ProjectTaskRow): string =>
  searchable(
    t.altTaskID,
    t.taskInfo?.name,
    t.taskInfo?.description,
    t.account?.altAccountCodeID,
    t.intermediatePath
  );

/** What a term is matched against on an intermediate node. */
export const nodeBlob = (n: ProjectTaskNode): string =>
  searchable(n.name, n.altIntermediateID);

/**
 * Why a node is on screen (the Projects page's three marks):
 * - `match`   its own fields hit the term — accented, subtree revealed whole
 * - `context` kept only because something beneath it matched — dimmed
 * - `plain`   inside a revealed subtree, so it needs no explanation and no
 *             dimming (the prototype gives these neither `.ctx` nor `.match`)
 */
export type NodeMark = 'match' | 'context' | 'plain';

export interface TaskFilterResult {
  /** Empty term — the component renders everything and skips the sets. */
  empty: boolean;
  /** `taskKey`s that survive the filter. */
  visibleTasks: Set<string>;
  /** Intermediate IDs that survive the filter. */
  visibleNodes: Set<number>;
  /** Why each surviving node is there. */
  nodeMarks: Map<number, NodeMark>;
  /** Nodes the filter must force open so its hits are reachable. */
  expandNodes: Set<number>;
  /** Task rows shown — the `n` of the `n of m` counter. */
  matches: number;
}

/** Every task row linked directly to one node (not its descendants). */
const rowsOf = (byNode: Map<number, ProjectTaskRow[]>, id: number): ProjectTaskRow[] =>
  byNode.get(id) ?? [];

/** Index the flat task list by `intermediateID` once, for the whole walk. */
export const tasksByIntermediate = (tasks: ProjectTaskRow[]): Map<number, ProjectTaskRow[]> => {
  const out = new Map<number, ProjectTaskRow[]>();
  tasks.forEach((t) => {
    if (t.intermediateID == null) return;
    const list = out.get(t.intermediateID);
    if (list) list.push(t);
    else out.set(t.intermediateID, [t]);
  });
  return out;
};

/**
 * Subtree roll-up for a node's `{open} of {total} open` count (design: "node
 * counts are a roll-up of the whole subtree"). The payload's `totalTasks` /
 * `remainingTasks` are per-node, so this arithmetic is the client's.
 *
 * `total` counts every task row in the subtree; `open` counts the subset with
 * `remainingAmount > 0`. `open` is therefore a subset of `total` by
 * construction and can never exceed it.
 */
export const subtreeCounts = (
  node: ProjectTaskNode,
  byNode: Map<number, ProjectTaskRow[]>
): { open: number; total: number } => {
  let open = 0;
  let total = 0;
  const visit = (n: ProjectTaskNode): void => {
    rowsOf(byNode, n.intermediateID).forEach((t) => {
      total += 1;
      if (t.remainingAmount > 0) open += 1;
    });
    (n.intermediates ?? []).forEach(visit);
  };
  visit(node);
  return { open, total };
};

/**
 * How many intermediates each `taskID` is linked to. `> 1` is the dual-link
 * anomaly the amber `1 budget · n locations` marker warns about — two rows,
 * one budget, so claiming against both draws the same pool down twice.
 */
export const dupCounts = (tasks: ProjectTaskRow[]): Record<number, number> => {
  const out: Record<number, number> = {};
  tasks.forEach((t) => { out[t.taskID] = (out[t.taskID] ?? 0) + 1; });
  return out;
};

/** Every task row beneath a node, itself included. */
const subtreeRows = (
  node: ProjectTaskNode,
  byNode: Map<number, ProjectTaskRow[]>
): ProjectTaskRow[] => {
  const out: ProjectTaskRow[] = [];
  const visit = (n: ProjectTaskNode): void => {
    out.push(...rowsOf(byNode, n.intermediateID));
    (n.intermediates ?? []).forEach(visit);
  };
  visit(node);
  return out;
};

/**
 * Reveal a matched node's whole subtree — every descendant node and every task
 * under them, matching or not. Descendants are `plain`, not `context`: they are
 * not standing in for something below, they are the revealed content itself.
 */
const keepWholeSubtree = (
  node: ProjectTaskNode,
  byNode: Map<number, ProjectTaskRow[]>,
  term: string,
  acc: TaskFilterResult
): void => {
  acc.visibleNodes.add(node.intermediateID);
  acc.nodeMarks.set(node.intermediateID, nodeBlob(node).includes(term) ? 'match' : 'plain');
  acc.expandNodes.add(node.intermediateID);
  rowsOf(byNode, node.intermediateID).forEach((t) => {
    acc.visibleTasks.add(taskKey(t));
  });
  (node.intermediates ?? []).forEach((c) => keepWholeSubtree(c, byNode, term, acc));
};

/** Returns true when the node survives the filter. */
const walkNode = (
  node: ProjectTaskNode,
  term: string,
  byNode: Map<number, ProjectTaskRow[]>,
  acc: TaskFilterResult
): boolean => {
  // "Show me everything under Building 2600" — the primary use of this filter.
  if (nodeBlob(node).includes(term)) {
    keepWholeSubtree(node, byNode, term, acc);
    return true;
  }

  const hits = rowsOf(byNode, node.intermediateID).filter((t) => taskBlob(t).includes(term));
  // Children first, so a matching grandchild keeps its whole ancestor chain.
  const keptChild = (node.intermediates ?? [])
    .map((c) => walkNode(c, term, byNode, acc))
    .some(Boolean);

  // Nothing matched anywhere beneath it → the intermediate is dropped entirely.
  if (hits.length === 0 && !keptChild) return false;

  // Kept as ancestor context for the hits below, and auto-expanded to expose them.
  acc.visibleNodes.add(node.intermediateID);
  acc.nodeMarks.set(node.intermediateID, 'context');
  acc.expandNodes.add(node.intermediateID);
  hits.forEach((t) => { acc.visibleTasks.add(taskKey(t)); });
  return true;
};

/**
 * Filter a whole catalog. `term` is normalized here, so callers can hand it the
 * raw input value. An empty term returns `empty: true` with empty sets — the
 * component renders the unfiltered catalog rather than consulting them.
 *
 * Both browse layouts read the same result: flat mode uses `visibleTasks` and
 * `matches`; tree mode additionally uses `visibleNodes`, `nodeMarks` (for the
 * dimmed-context treatment) and `expandNodes`.
 */
export const filterProjectTasks = (
  catalog: ProjectTasksResponse | null,
  rawTerm: string
): TaskFilterResult => {
  const acc: TaskFilterResult = {
    empty: true,
    visibleTasks: new Set<string>(),
    visibleNodes: new Set<number>(),
    nodeMarks: new Map<number, NodeMark>(),
    expandNodes: new Set<number>(),
    matches: 0,
  };
  const term = normalizeTerm(rawTerm ?? '');
  if (!catalog) return acc;
  if (!term) {
    acc.matches = catalog.tasks.length;
    return acc;
  }
  acc.empty = false;

  const byNode = tasksByIntermediate(catalog.tasks);

  // Direct tasks have no node to hang off — they match on their own blob only.
  catalog.tasks.forEach((t) => {
    if (t.intermediateID != null) return;
    if (taskBlob(t).includes(term)) acc.visibleTasks.add(taskKey(t));
  });

  (catalog.intermediates ?? []).forEach((n) => walkNode(n, term, byNode, acc));

  // A link whose intermediate is gone from the tree (API note 3) is still
  // claimable, so it must still be findable — it is in neither the direct pass
  // above nor any node walk, so sweep for it here.
  const inTree = new Set<number>();
  const mark = (n: ProjectTaskNode): void => {
    inTree.add(n.intermediateID);
    (n.intermediates ?? []).forEach(mark);
  };
  (catalog.intermediates ?? []).forEach(mark);
  catalog.tasks.forEach((t) => {
    if (t.intermediateID == null || inTree.has(t.intermediateID)) return;
    if (taskBlob(t).includes(term)) acc.visibleTasks.add(taskKey(t));
  });

  // The `n` of `n of m` describes the LIST, so it is what survived, revealed
  // rows included — not a separate count of blob hits that could disagree.
  acc.matches = acc.visibleTasks.size;
  return acc;
};

/** Every intermediate ID in a tree — for "expand everything the term hit". */
export const allNodeIDs = (nodes: ProjectTaskNode[]): number[] => {
  const out: number[] = [];
  const visit = (n: ProjectTaskNode): void => {
    out.push(n.intermediateID);
    (n.intermediates ?? []).forEach(visit);
  };
  (nodes ?? []).forEach(visit);
  return out;
};

export { subtreeRows };
